import { memo, type ReactNode } from "react"
import { Disclaimer } from "@/components/resources/components/Disclaimer"
import { ArticleHero } from "@/components/resources/components/Hero"
import { ArticleSchema } from "@/components/resources/components/Schemas"
import { TableOfContentsLayout } from "@/components/resources/components/TableOfContents"
import { TeaserGrid } from "@/components/resources/components/TeaserGrid"
import { NewsletterTeaser } from "@/components/resources/components/TeaserGrid/NewsletterTeaser"
import { ScrollOffsetProvider } from "@/components/resources/context/ScrollPage/ScrollPageContext"
import type { Article } from "@/components/resources/data/posts.types"

const ArticleContent = memo(
  ({ children }: { children: ReactNode }) => (
    <>
      <div className="max-w-3xl mx-auto px-4">
        {children}
        <Disclaimer />
      </div>
      <div className="flex justify-center mb-8 md:mb-12 lg:mb-16 pt-8 md:pt-12 lg:pt-16 max-w-5xl mx-auto px-4">
        <NewsletterTeaser as="section" />
      </div>
    </>
  ),
  () => true,
) // A post's body never changes, so the table of contents can re-render the layout without it.
// The key swaps the body when the reader opens another post through the same route.

ArticleContent.displayName = "ArticleContentRenderer"

/** The post page around a rendered body: hero, table of contents, disclaimer, newsletter, related posts. */
export const ArticlePage = ({ children, ...article }: Article & { children: ReactNode }) => {
  const {
    title,
    subtitle,
    categoryName,
    image,
    author,
    publishedAt,
    related,
    category,
    slug,
    releaseNumber,
    projectMeta,
    tableOfContents,
  } = article
  return (
    <article className="mb-8 md:mb-12 lg:mb-16">
      <div className="max-w-7xl mx-auto px-4">
        <ArticleHero
          categoryName={categoryName}
          category={category}
          title={title}
          subtitle={subtitle}
          image={image || undefined}
          publishedAt={publishedAt}
          releaseNumber={releaseNumber}
          projectMeta={projectMeta}
          author={author}
        />
      </div>
      {tableOfContents ? (
        // Not ScrollPageProvider: its scroll on load conflicts with the scroll spy on a single article
        <ScrollOffsetProvider scrollOffset={100}>
          <TableOfContentsLayout tableOfContents={tableOfContents}>
            <ArticleContent key={`${category}/${slug}`}>{children}</ArticleContent>
          </TableOfContentsLayout>
        </ScrollOffsetProvider>
      ) : (
        <ArticleContent key={`${category}/${slug}`}>{children}</ArticleContent>
      )}
      {related?.length ? (
        <section className="pt-8 md:pt-12 lg:pt-16 mb-8 md:mb-12 lg:mb-16 max-w-7xl mx-auto px-4">
          <div className="flex flex-col gap-6">
            <h2 className="text-xl font-medium">Continue reading</h2>
            <TeaserGrid teasers={related} />
          </div>
        </section>
      ) : null}
      <ArticleSchema article={article} />
    </article>
  )
}
